import {
  MultiSelect,
  Checkbox,
  Group,
  Loader,
  Box,
  OptionsFilter,
  Text,
} from "@mantine/core"
import { useState, useRef, useEffect } from "react"

import { optionType } from "@costory/front/components/QueryBuilder"
import { getAsyncData } from "@costory/front/components/queryBuilder/CEL/searchUtils"

const SELECT_ALL_VALUE = "__SELECT_ALL__"

interface MantineMultiSelectProps {
  data: string[]
  value?: string[]
  field: string
  onChange?: (values: string[]) => void
  placeholder?: string
  disabled?: boolean
  searchThreshold?: number
}

export const MantineMultiSelectComponent = ({
  data,
  value = [],
  field,
  onChange,
  placeholder = "Select cloud providers",
  disabled = false,
  searchThreshold = 5,
}: MantineMultiSelectProps) => {
  const [searchValue, setSearchValue] = useState("")
  const [filteredData, setFilteredData] = useState([...data])
  const [loading, setLoading] = useState(false)
  const abortController = useRef<AbortController | undefined>(undefined)

  useEffect(() => {
    setFilteredData([...data])
    setSearchValue("")
    setLoading(false)
    abortController.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field])

  const options = filteredData.map((item) => ({ value: item, label: item }))
  const axesOptions =
    options.length === 0
      ? []
      : [
          {
            value: SELECT_ALL_VALUE,
            label: searchValue.trim() ? "Select All (Filtered)" : "Select All",
          },
          ...options,
        ]
  const getVisibleOptionsForState = () => {
    if (!searchValue.trim()) {
      return options
    }
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchValue.toLowerCase().trim()),
    )
  }

  const visibleOptionsForState = getVisibleOptionsForState()
  const allFilteredSelected =
    visibleOptionsForState.length > 0 &&
    visibleOptionsForState.every((opt) => value.includes(opt.value))
  const displayValues = value.filter((v) => v !== SELECT_ALL_VALUE)
  const allFilteredValues = options.map((opt) => opt.value)

  const fetchOptions = (query: string) => {
    abortController.current?.abort()
    abortController.current = new AbortController()
    setLoading(true)

    getAsyncData(query, abortController.current!.signal).then((result) => {
      const values = result
        .filter((item) => item.field === field)
        .flatMap((item) => item.values || [])
      setFilteredData(values)
      setLoading(false)
      abortController.current = undefined
    })
  }

  const handleSearchChange = (query: string) => {
    setSearchValue(query)

    if (!query?.trim()) {
      setFilteredData([...data])
      setLoading(false)
      abortController.current?.abort()
      return
    }

    const filtered = data.filter((item) =>
      item.toLowerCase().includes(query.toLowerCase()),
    )

    if (filtered.length < searchThreshold) {
      fetchOptions(query)
    } else {
      setFilteredData(filtered)
    }
  }

  const filterOptions: OptionsFilter = ({ options, search }) => {
    if (!search) return options

    const filtered = options.filter((item) => {
      const optionItem = item as optionType
      return (
        optionItem.value !== SELECT_ALL_VALUE &&
        (optionItem.label || "")
          .toLowerCase()
          .includes(search.toLowerCase().trim())
      )
    })

    if (filtered.length === 0) return []

    const selectAllOption = options.find(
      (item) => (item as optionType).value === SELECT_ALL_VALUE,
    )
    return selectAllOption ? [selectAllOption, ...filtered] : filtered
  }

  const handleSelectAllToggle = () => {
    const currentVisibleValues = visibleOptionsForState.map((opt) => opt.value)
    const existingValues = value.filter(
      (v) => !currentVisibleValues.includes(v),
    )

    onChange?.(
      allFilteredSelected
        ? existingValues
        : [...existingValues, ...currentVisibleValues],
    )
  }

  const renderOption = ({
    option,
    checked,
  }: {
    option: optionType
    checked?: boolean
  }) => {
    if (!option) return null

    const isSelectAll = option.value === SELECT_ALL_VALUE

    const handleRowClick = (e: React.MouseEvent) => {
      if (isSelectAll) {
        e.preventDefault()
        e.stopPropagation()
        handleSelectAllToggle()
      }
    }

    const handleCheckboxClick = (e: React.MouseEvent) => {
      if (isSelectAll) {
        e.stopPropagation()
      }
    }

    return (
      <Group
        flex={1}
        gap="sm"
        style={{ cursor: isSelectAll ? "pointer" : "default" }}
        onClick={handleRowClick}
      >
        <Checkbox
          checked={isSelectAll ? allFilteredSelected : (checked ?? false)}
          onChange={isSelectAll ? handleSelectAllToggle : () => {}}
          onClick={handleCheckboxClick}
          tabIndex={-1}
          styles={{ input: { cursor: "pointer" } }}
        />
        <Text size="sm">{option.label}</Text>
      </Group>
    )
  }

  const handleChange = (values: string[]) => {
    if (!Array.isArray(values)) return

    const hasSelectAll = values.includes(SELECT_ALL_VALUE)
    const hadSelectAll = value.includes(SELECT_ALL_VALUE)

    if (hasSelectAll && !hadSelectAll) {
      onChange?.([
        ...value.filter((v) => !allFilteredValues.includes(v)),
        ...allFilteredValues,
      ])
    } else if (!hasSelectAll && hadSelectAll) {
      onChange?.(value.filter((v) => !allFilteredValues.includes(v)))
    } else {
      onChange?.(values.filter((v) => v !== SELECT_ALL_VALUE))
    }
  }

  return (
    <Box>
      <MultiSelect
        data={axesOptions}
        value={displayValues}
        onChange={handleChange}
        searchable
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        placeholder={placeholder}
        disabled={disabled}
        nothingFoundMessage="No options"
        clearable
        maxDropdownHeight={300}
        leftSection={loading ? <Loader size={16} /> : undefined}
        styles={{
          pill: {
            flexShrink: 0,
            whiteSpace: "nowrap",
          },
          pillsList: {
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
            maxWidth: "100%",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          },
        }}
        filter={filterOptions}
        renderOption={renderOption}
      />
    </Box>
  )
}
